import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import TeacherHeader from "./TeacherHeader";
import ClassesManager from "@/components/ClassesManager";
import NewSessionDialog from "@/components/NewSessionDialog";
import NewLoginAlertBanner from "@/components/NewLoginAlertBanner";
import LastActivityWidget from "@/components/LastActivityWidget";
import OnboardingTour from "@/components/OnboardingTour";
import { ChemText } from "@/components/ChemText";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, LabelList,
} from "recharts";
import {
  Plus, Play, Trash2, ExternalLink, Clock, Hash, Users, BookOpen,
  GraduationCap, TrendingUp, AlertTriangle, ListChecks, Layers, Calendar,
  ChevronDown, ChevronRight, Archive, Copy, MessageSquareWarning, Sparkles,
} from "lucide-react";

const BAR_COLORS = ["#10B981", "#22C55E", "#84CC16", "#EAB308", "#F97316", "#EF4444"];

function colorForPercent(p) {
  if (p >= 80) return "#10B981";
  if (p >= 60) return "#84CC16";
  if (p >= 40) return "#EAB308";
  if (p >= 20) return "#F97316";
  return "#EF4444";
}

export default function TeacherDashboard() {
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [overview, setOverview] = useState(null);
  const [scopeClass, setScopeClass] = useState("all"); // "all" or class.id
  const [openCreate, setOpenCreate] = useState(false);
  const [openClasses, setOpenClasses] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [draftToOpen, setDraftToOpen] = useState(null);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [me, setMe] = useState(null);
  const [impact, setImpact] = useState(null);
  const BETA_BANNER_KEY = "kartkowki:beta-thanks-banner-dismissed-v1";
  const [betaBannerHidden, setBetaBannerHidden] = useState(() => {
    try { return localStorage.getItem(BETA_BANNER_KEY) === "1"; }
    catch { return false; }
  });
  const dismissBetaBanner = () => {
    try { localStorage.setItem(BETA_BANNER_KEY, "1"); } catch { /* noop */ }
    setBetaBannerHidden(true);
  };
  // Per-(class, school-year) collapsed state, persisted in localStorage.
  // Default: collapsed=true for past school years.
  const HIST_KEY = "kartkowki:history-collapsed-v1";
  const [historyCollapsed, setHistoryCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(HIST_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const setCollapsed = (key, value) => {
    setHistoryCollapsed((prev) => {
      const next = { ...prev, [key]: value };
      try { localStorage.setItem(HIST_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };
  const isCollapsed = (key) => (historyCollapsed[key] === undefined ? true : historyCollapsed[key]);
  const nav = useNavigate();

  const loadOverview = useCallback(async (classFilter) => {
    try {
      const params = {};
      if (classFilter && classFilter !== "all") params.class_id = classFilter;
      const { data } = await teacherApi.get("/dashboard/overview", { params });
      setOverview(data);
    } catch (e) {
      if (e.response?.status === 401) nav("/teacher/login");
    }
  }, [nav]);

  const load = useCallback(async () => {
    try {
      const [s, c, d] = await Promise.all([
        teacherApi.get("/sessions"),
        teacherApi.get("/classes"),
        teacherApi.get("/drafts"),
      ]);
      setSessions(s.data);
      setClasses(c.data);
      setDrafts(d.data.drafts || []);
    } catch (e) {
      if (e.response?.status === 401) nav("/teacher/login");
    }
  }, [nav]);

  const loadRef = useRef(load);
  const loadOverviewRef = useRef(loadOverview);
  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => { loadOverviewRef.current = loadOverview; }, [loadOverview]);

  useEffect(() => { loadRef.current?.(); }, []);
  useEffect(() => { loadOverviewRef.current?.(scopeClass); }, [scopeClass]);

  // Load current teacher profile once — used for the beta-tester thank-you banner.
  useEffect(() => {
    teacherApi.get("/auth/me")
      .then(({ data }) => setMe(data))
      .catch(() => { /* ignore, banner just stays hidden */ });
  }, []);

  // Fetch beta-tester impact only when the user actually is one — avoids
  // an unnecessary DB roundtrip for non-tester accounts.
  useEffect(() => {
    if (!me?.beta_tester) return;
    teacherApi.get("/tester/impact")
      .then(({ data }) => setImpact(data))
      .catch(() => { /* silent — the strip just won't render */ });
  }, [me?.beta_tester]);

  // Auto-end notifications — cron flips a session to `ended` after the time
  // limit expires and drops an unread notification. On dashboard load we
  // show one toast per unread notification, then mark them read so we don't
  // repeat on refresh. Loud enough to be noticed, brief enough to dismiss.
  useEffect(() => {
    let cancelled = false;
    teacherApi.get("/notifications/unread")
      .then(({ data }) => {
        if (cancelled || !Array.isArray(data)) return;
        data.forEach((n) => {
          if (n.kind === "session_auto_ended") {
            toast.success(
              `⏱ Kartkówka ${n.session_code} zakończyła się automatycznie` +
                (n.auto_submitted_count
                  ? ` — oceniono ${n.auto_submitted_count} nieoddanych prac.`
                  : "."),
              { duration: 8000 }
            );
            teacherApi.post(`/notifications/${n.id}/mark-read`).catch(() => {});
          }
        });
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  const deleteSession = async () => {
    if (!toDelete) return;
    try {
      await teacherApi.delete(`/sessions/${toDelete.id}`);
      toast.success("Sesja usunięta");
      load();
      loadOverview(scopeClass);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się usunąć");
    } finally {
      setToDelete(null);
    }
  };

  const [duplicatingId, setDuplicatingId] = useState(null);
  const [duplicateSrc, setDuplicateSrc] = useState(null);
  const runDuplicate = async (src, targetClassId) => {
    if (duplicatingId) return;
    setDuplicatingId(src.id);
    setDuplicateSrc(null);
    try {
      const payload = targetClassId !== undefined ? { class_id: targetClassId } : {};
      const { data } = await teacherApi.post(`/sessions/${src.id}/duplicate`, payload);
      toast.success(`Skopiowano — nowy kod ${data.code}`);
      await load();
      loadOverview(scopeClass);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się zduplikować");
    } finally {
      setDuplicatingId(null);
    }
  };

  const deleteDraft = async () => {
    if (!draftToDelete) return;
    try {
      await teacherApi.delete(`/drafts/${draftToDelete.id}`);
      toast.success("Szkic usunięty");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się usunąć szkicu");
    } finally {
      setDraftToDelete(null);
    }
  };

  const continueDraft = (draft) => {
    setDraftToOpen(draft);
    setOpenCreate(true);
  };

  const statusBadge = (status) => {
    const map = {
      lobby: { bg: "bg-[#FEF08A]", text: "Poczekalnia" },
      active: { bg: "bg-[#D1FAE5]", text: "Aktywna" },
      ended: { bg: "bg-[#E0E7FF]", text: "Zakończona" },
    };
    const m = map[status] || map.lobby;
    return (
      <span className={`brut ${m.bg} text-xs tracking-[0.15em] uppercase font-bold px-2 py-1 rounded-md inline-block`}>
        {m.text}
      </span>
    );
  };

  // ---------- School-year helpers ----------
  // Polish school year runs Sept 1 → Aug 31. Sessions before the current
  // school year's Sept 1 are considered historic and rendered collapsed.
  const schoolYearOf = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    // Year that starts the school year for this date
    const startYear = d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1;
    return { startYear, label: `${startYear}/${startYear + 1}` };
  };
  const now = new Date();
  const currentSchoolYearStart = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  const isCurrentSchoolYear = (iso) => {
    const y = schoolYearOf(iso);
    return y ? y.startYear === currentSchoolYearStart : true;
  };

  // Filter sessions list by selected class scope for the bottom list
  const filteredSessions = scopeClass === "all"
    ? sessions
    : sessions.filter((s) => s.class_id === scopeClass);

  // Group sessions by class, with lobby sessions on top within each group
  const STATUS_ORDER = { lobby: 0, active: 1, ended: 2 };
  const sortByStatusThenDate = (items) => {
    items.sort((a, b) => {
      const sa = STATUS_ORDER[a.status] ?? 9;
      const sb = STATUS_ORDER[b.status] ?? 9;
      if (sa !== sb) return sa - sb;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return items;
  };

  const groupedSessions = (() => {
    const map = new Map(); // class_name -> { class_id, class_name, items[] }
    for (const s of filteredSessions) {
      const key = s.class_name || "Bez klasy";
      if (!map.has(key)) {
        map.set(key, {
          class_id: s.class_id || null,
          class_name: key,
          unassigned: !s.class_id,
          items: [],
        });
      }
      map.get(key).items.push(s);
    }
    for (const g of map.values()) {
      sortByStatusThenDate(g.items);
      // Split into current school year + per-past-year sub-groups
      g.current = g.items.filter((s) => isCurrentSchoolYear(s.created_at));
      const pastMap = new Map(); // label -> items[]
      for (const s of g.items) {
        if (isCurrentSchoolYear(s.created_at)) continue;
        const y = schoolYearOf(s.created_at);
        if (!y) continue;
        if (!pastMap.has(y.label)) pastMap.set(y.label, { startYear: y.startYear, label: y.label, items: [] });
        pastMap.get(y.label).items.push(s);
      }
      // Past years sorted newest first
      g.pastYears = Array.from(pastMap.values()).sort((a, b) => b.startYear - a.startYear);
      for (const py of g.pastYears) sortByStatusThenDate(py.items);
    }
    // Order groups: classes alphabetically, "Bez klasy" last; classes with any lobby session bubble up
    return Array.from(map.values()).sort((a, b) => {
      if (a.unassigned !== b.unassigned) return a.unassigned ? 1 : -1;
      const aHasLobby = a.items.some((s) => s.status === "lobby") ? 0 : 1;
      const bHasLobby = b.items.some((s) => s.status === "lobby") ? 0 : 1;
      if (aHasLobby !== bHasLobby) return aHasLobby - bHasLobby;
      return a.class_name.localeCompare(b.class_name, "pl");
    });
  })();

  const formatDateTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const date = d.toLocaleDateString("pl-PL", {
        day: "2-digit", month: "2-digit", year: "numeric",
      });
      const time = d.toLocaleTimeString("pl-PL", {
        hour: "2-digit", minute: "2-digit",
      });
      return `${date}, ${time}`;
    } catch {
      return "—";
    }
  };

  const t = overview?.totals || {};
  const recent = overview?.recent_sessions || [];
  const distribution = overview?.score_distribution || [];
  const hardest = overview?.hardest_questions || [];
  const topTopics = overview?.top_topics || [];
  const perClass = overview?.per_class || [];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <TeacherHeader />
      <OnboardingTour />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <NewLoginAlertBanner />
        <LastActivityWidget />
        {/* Beta tester thank-you banner */}
        {me?.beta_tester && !betaBannerHidden && (
          <div
            className="brut border-2 border-[#0F172A] bg-[#DDD6FE] rounded-md p-4 mb-6 flex items-start gap-3"
            data-testid="beta-tester-banner"
            role="status"
          >
            <span className="text-2xl leading-none flex-shrink-0" aria-hidden>🧪</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <b className="font-display font-black text-base">Dziękujemy, że testujesz betę!</b>
                <span className="brut bg-white text-[10px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded">
                  Tester
                </span>
              </div>
              <p className="text-sm text-[#334155] mt-1">
                Twoje pytania, zgłoszenia i propozycje realnie kształtują tę aplikację.
                Coś nie działa? Kliknij <b>„Feedback"</b> w prawym dolnym rogu — czytamy każde zgłoszenie.
              </p>
            </div>
            <button
              onClick={dismissBetaBanner}
              className="brut brut-hover bg-white border-2 border-[#0F172A] rounded-md p-1 flex-shrink-0"
              title="Ukryj to podziękowanie"
              aria-label="Zamknij banner"
              data-testid="beta-tester-banner-dismiss"
            >
              <span aria-hidden>×</span>
            </button>
          </div>
        )}
        {/* Beta tester impact strip */}
        {me?.beta_tester && impact && (
          <div className="mb-6" data-testid="tester-impact-strip">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-[#7C3AED]" strokeWidth={2.5} />
              <span className="text-[11px] tracking-[0.2em] uppercase font-black text-[#334155]">
                Twój wkład w betę
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <ImpactStat
                testId="impact-questions"
                label="Twoje pytania"
                value={impact.questions_added}
                hint="w Twojej prywatnej puli"
                icon={<BookOpen className="w-4 h-4" strokeWidth={2.5} />}
                color="#D1FAE5"
              />
              <ImpactStat
                testId="impact-proposals-approved"
                label="Zaakceptowane propozycje"
                value={impact.proposals_approved}
                hint="trafiły do wspólnej puli"
                icon={<Sparkles className="w-4 h-4" strokeWidth={2.5} />}
                color="#FEF08A"
                badge={impact.proposals_pending > 0
                  ? `+${impact.proposals_pending} w kolejce`
                  : null}
              />
              <ImpactStat
                testId="impact-sessions"
                label="Uruchomione kartkówki"
                value={impact.sessions_run}
                hint="sesje z uczniami"
                icon={<ListChecks className="w-4 h-4" strokeWidth={2.5} />}
                color="#E0F2FE"
              />
              <ImpactStat
                testId="impact-feedback"
                label="Zgłoszenia i sugestie"
                value={impact.feedback_submitted}
                hint="raporty i pomysły"
                icon={<MessageSquareWarning className="w-4 h-4" strokeWidth={2.5} />}
                color="#FFEDD5"
              />
              <div
                className="brut border-2 border-[#0F172A] bg-[#0F172A] text-white rounded-md p-3 flex flex-col justify-between"
                data-testid="impact-thanks"
              >
                <div className="text-[11px] uppercase tracking-wide text-[#FEF08A] font-black">
                  🧪 Dzięki Tobie
                </div>
                <div className="text-xs text-[#CBD5E1] mt-1 leading-snug">
                  Każde pytanie i zgłoszenie od Ciebie realnie kształtuje aplikację.
                  Trzymaj tak dalej!
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B] mb-2">Witaj nauczycielu</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl">Dashboard</h1>
            {overview?.scope?.class_name && (
              <div className="text-sm text-[#64748B] mt-1">
                Filtr: <span className="font-bold">{overview.scope.class_name}</span>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <Select value={scopeClass} onValueChange={setScopeClass}>
              <SelectTrigger
                className="brut bg-white border-2 border-[#0F172A] rounded-md h-12 px-4 font-bold min-w-[180px]"
                data-testid="dashboard-class-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-2 border-[#0F172A] rounded-md">
                <SelectItem value="all">Wszystkie klasy</SelectItem>
                {classes.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`scope-class-${c.name}`}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={() => setOpenClasses(true)}
              variant="outline"
              className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md h-12 px-4"
              data-testid="manage-classes-btn"
            >
              <GraduationCap className="w-4 h-4 mr-2" strokeWidth={2.5} />Klasy ({classes.length})
            </Button>
            <Button
              onClick={() => setOpenCreate(true)}
              className="brut brut-hover bg-[#FEF08A] hover:bg-[#FEF08A] text-[#0F172A] font-bold rounded-md h-12 px-6"
              data-testid="create-session-btn"
            >
              <Plus className="w-5 h-5 mr-2" strokeWidth={2.5} />Nowa kartkówka
            </Button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="brut bg-[#D1FAE5] rounded-md p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs tracking-[0.2em] uppercase font-bold">Pytania</div>
              <BookOpen className="w-4 h-4" strokeWidth={2.5} />
            </div>
            <div className="font-display font-black text-3xl mt-2" data-testid="kpi-questions">{t.questions_pool || 0}</div>
            <Link to="/teacher/questions" className="text-xs font-bold inline-flex items-center gap-1 mt-1 hover:underline">
              Zarządzaj <ExternalLink className="w-3 h-3" strokeWidth={2.5} />
            </Link>
          </div>
          <div className="brut bg-[#E0E7FF] rounded-md p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs tracking-[0.2em] uppercase font-bold">Sesje</div>
              <Hash className="w-4 h-4" strokeWidth={2.5} />
            </div>
            <div className="font-display font-black text-3xl mt-2" data-testid="kpi-sessions">{t.sessions || 0}</div>
            <div className="text-xs text-[#64748B] mt-1">aktywne: {t.active_sessions || 0}</div>
          </div>
          <div className="brut bg-[#FFEDD5] rounded-md p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs tracking-[0.2em] uppercase font-bold">Uczniowie</div>
              <Users className="w-4 h-4" strokeWidth={2.5} />
            </div>
            <div className="font-display font-black text-3xl mt-2" data-testid="kpi-students">{t.unique_students || 0}</div>
            <div className="text-xs text-[#64748B] mt-1">unikalne nazwiska</div>
          </div>
          <div className="brut bg-[#FCE7F3] rounded-md p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs tracking-[0.2em] uppercase font-bold">Oddane</div>
              <ListChecks className="w-4 h-4" strokeWidth={2.5} />
            </div>
            <div className="font-display font-black text-3xl mt-2" data-testid="kpi-submissions">{t.submissions || 0}</div>
            <div className="text-xs text-[#64748B] mt-1">arkusze</div>
          </div>
          <div className="brut bg-[#FEF08A] rounded-md p-4 col-span-2 lg:col-span-1">
            <div className="flex items-center justify-between">
              <div className="text-xs tracking-[0.2em] uppercase font-bold">Średnia</div>
              <TrendingUp className="w-4 h-4" strokeWidth={2.5} />
            </div>
            <div className="font-display font-black text-3xl mt-2" data-testid="kpi-avg">{t.class_average || 0}%</div>
            <div className="text-xs text-[#64748B] mt-1">wszystkich oddań</div>
          </div>
        </div>

        {/* Charts row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Recent sessions bar chart */}
          <section className="brut bg-white rounded-md p-5" data-testid="chart-recent-sessions">
            <h2 className="font-display font-black text-xl mb-1">Ostatnie kartkówki — średnia klasy</h2>
            <p className="text-xs text-[#64748B] mb-3">{recent.length} ostatnich sesji w zakresie filtra</p>
            {recent.length === 0 ? (
              <div className="text-sm text-[#64748B] py-12 text-center">Brak danych do wyświetlenia.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={recent} margin={{ top: 16, right: 8, bottom: 0, left: -16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="code" tick={{ fontSize: 11, fontWeight: 600 }} stroke="#0F172A" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#0F172A" />
                  <Tooltip
                    contentStyle={{
                      border: "2px solid #0F172A", borderRadius: 6, background: "#fff",
                      fontWeight: 600, fontSize: 12,
                    }}
                    formatter={(value, _name, ctx) => [
                      `${value}% · ${ctx.payload.students} uczniów`, ctx.payload.title,
                    ]}
                    labelFormatter={(l, payload) => {
                      const p = payload?.[0]?.payload;
                      return p ? `${p.code}${p.class_name ? ` · ${p.class_name}` : ""}` : l;
                    }}
                  />
                  <Bar dataKey="average_percent" radius={[4, 4, 0, 0]}>
                    {recent.map((s, idx) => (
                      <Cell key={idx} fill={colorForPercent(s.average_percent)} stroke="#0F172A" strokeWidth={2} />
                    ))}
                    <LabelList dataKey="average_percent" position="top" formatter={(v) => `${v}%`} style={{ fontWeight: 700, fontSize: 11 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </section>

          {/* Score distribution */}
          <section className="brut bg-white rounded-md p-5" data-testid="chart-score-distribution">
            <h2 className="font-display font-black text-xl mb-1">Rozkład wyników</h2>
            <p className="text-xs text-[#64748B] mb-3">Liczba arkuszy w przedziałach % w zakresie filtra</p>
            {distribution.every((b) => b.count === 0) ? (
              <div className="text-sm text-[#64748B] py-12 text-center">Brak oddanych arkuszy.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={distribution} margin={{ top: 16, right: 8, bottom: 0, left: -16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fontWeight: 600 }} stroke="#0F172A" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#0F172A" />
                  <Tooltip
                    contentStyle={{ border: "2px solid #0F172A", borderRadius: 6, background: "#fff", fontWeight: 600, fontSize: 12 }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {distribution.map((b, idx) => (
                      <Cell key={idx} fill={BAR_COLORS[BAR_COLORS.length - 1 - idx] || "#0F172A"} stroke="#0F172A" strokeWidth={2} />
                    ))}
                    <LabelList dataKey="count" position="top" style={{ fontWeight: 700, fontSize: 11 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </section>
        </div>

        {/* Lists row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <section className="brut bg-white rounded-md p-5" data-testid="hardest-questions-card">
            <h2 className="font-display font-black text-xl mb-1 inline-flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#EF4444]" strokeWidth={2.5} />
              Top 5 najtrudniejszych pytań
            </h2>
            <p className="text-xs text-[#64748B] mb-3">Najniższy success-rate w zakresie filtra</p>
            {hardest.length === 0 ? (
              <div className="text-sm text-[#64748B] py-6 text-center">Brak danych — uczniowie jeszcze nie oddali kartkówek.</div>
            ) : (
              <ol className="space-y-2">
                {hardest.map((q, idx) => (
                  <li
                    key={q.question_id}
                    className="brut bg-[#FEE2E2] rounded-md p-3 flex items-start gap-3"
                    data-testid={`hardest-q-${idx}`}
                  >
                    <span className="brut bg-white w-8 h-8 flex items-center justify-center rounded-md font-display font-black flex-shrink-0">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      {q.topic && (
                        <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#64748B]">{q.topic}</div>
                      )}
                      <div className="font-bold text-sm leading-snug"><ChemText text={q.text} /></div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-display font-black text-xl">{q.success_rate}%</div>
                      <div className="text-xs text-[#64748B]">{q.correct}/{q.attempts}</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <section className="brut bg-white rounded-md p-5" data-testid="top-topics-card">
            <h2 className="font-display font-black text-xl mb-1 inline-flex items-center gap-2">
              <Layers className="w-5 h-5" strokeWidth={2.5} />
              Top 5 tematów
            </h2>
            <p className="text-xs text-[#64748B] mb-3">Najczęściej losowane w kartkówkach</p>
            {topTopics.length === 0 ? (
              <div className="text-sm text-[#64748B] py-6 text-center">Brak danych.</div>
            ) : (
              <ol className="space-y-2">
                {topTopics.map((row, idx) => {
                  const max = topTopics[0]?.count || 1;
                  const pct = Math.round((row.count / max) * 100);
                  return (
                    <li key={row.topic} className="brut bg-[#E0F2FE] rounded-md p-3" data-testid={`top-topic-${idx}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="brut bg-white w-7 h-7 flex items-center justify-center rounded-md font-display font-black text-sm flex-shrink-0">
                            {idx + 1}
                          </span>
                          <span className="font-bold truncate" title={row.topic}>{row.topic}</span>
                        </div>
                        <span className="font-display font-black">{row.count}</span>
                      </div>
                      <div className="h-2 bg-white rounded-md overflow-hidden border-2 border-[#0F172A]">
                        <div className="h-full bg-[#0F172A]" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        </div>

        {/* Per-class section: only when scope is "all" */}
        {scopeClass === "all" && perClass.length > 0 && (
          <section className="brut bg-white rounded-md p-5 mb-8" data-testid="per-class-card">
            <h2 className="font-display font-black text-xl mb-1 inline-flex items-center gap-2">
              <GraduationCap className="w-5 h-5" strokeWidth={2.5} />
              Ranking klas
            </h2>
            <p className="text-xs text-[#64748B] mb-3">Średnia z wszystkich oddań w danej klasie</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {perClass.map((row, idx) => (
                <div
                  key={row.class_id}
                  className="brut bg-[#F8FAFC] rounded-md p-3"
                  data-testid={`per-class-${row.class_name}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-bold">{idx + 1}. {row.class_name}</div>
                    <div className="font-display font-black text-2xl" style={{ color: colorForPercent(row.average_percent) }}>
                      {row.average_percent}%
                    </div>
                  </div>
                  <div className="text-xs text-[#64748B] mt-1">
                    {row.submissions} oddań · {row.sessions} sesji
                  </div>
                  <div className="mt-2 h-2 bg-white rounded-md overflow-hidden border-2 border-[#0F172A]">
                    <div
                      className="h-full"
                      style={{
                        width: `${Math.min(100, row.average_percent)}%`,
                        background: colorForPercent(row.average_percent),
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Drafts */}
        {drafts.length > 0 && (
          <section className="mb-8" data-testid="drafts-section">
            <div className="flex items-center gap-3 mb-3">
              <h2 className="font-display font-black text-2xl">Moje szkice</h2>
              <span className="brut bg-[#FEF08A] text-xs tracking-[0.15em] uppercase font-bold px-2 py-1 rounded-md">
                {drafts.length}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {drafts.map((d) => {
                const cls = classes.find((c) => c.id === d.class_id);
                const scopeLabel = (overview?.scopes || []).find?.((s) => s.scope === d.scope)?.label
                  || d.scope;
                return (
                  <div key={d.id} className="brut bg-[#FFFBEB] rounded-md p-4 flex flex-col gap-2"
                       data-testid={`draft-card-${d.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-display font-black text-lg truncate" title={d.name}>
                        {d.name}
                      </div>
                      <span className="text-xs text-[#64748B] flex-shrink-0">
                        {new Date(d.updated_at).toLocaleDateString("pl-PL", { day: "2-digit", month: "short" })}
                      </span>
                    </div>
                    <div className="text-xs text-[#64748B] font-bold">
                      {d.num_questions} pyt · {d.time_limit_minutes} min · {scopeLabel}
                      {cls ? ` · ${cls.name}` : ""}
                    </div>
                    <div className="flex gap-2 mt-1">
                      <Button
                        onClick={() => continueDraft(d)}
                        className="brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md h-9 px-3 text-xs flex-1"
                        data-testid={`draft-continue-${d.id}`}
                      >
                        <Play className="w-3 h-3 mr-1" strokeWidth={2.5} />Kontynuuj
                      </Button>
                      <Button
                        onClick={() => setDraftToDelete(d)}
                        variant="outline"
                        className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md h-9 px-3"
                        data-testid={`draft-delete-${d.id}`}
                        aria-label="Usuń szkic"
                      >
                        <Trash2 className="w-3 h-3" strokeWidth={2.5} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Sessions list */}
        <h2 className="font-display font-black text-2xl mb-4">
          Lista sesji
          {scopeClass !== "all" && classes.find((c) => c.id === scopeClass) && (
            <span className="text-base text-[#64748B] font-normal ml-2">
              · {classes.find((c) => c.id === scopeClass)?.name}
            </span>
          )}
        </h2>
        {filteredSessions.length === 0 ? (
          <div className="brut bg-white rounded-md p-10 text-center" data-testid="empty-sessions">
            <p className="text-[#64748B]">
              {scopeClass === "all"
                ? "Brak sesji. Utwórz pierwszą kartkówkę."
                : "Brak sesji w tej klasie."}
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {groupedSessions.map((group) => {
              // Counters reflect only the current school year (lobby/active are by definition recent)
              const lobbyCount = group.current.filter((s) => s.status === "lobby").length;
              const activeCount = group.current.filter((s) => s.status === "active").length;

              const renderCard = (s) => (
                <div key={s.id} className="brut brut-hover bg-white rounded-md p-6" data-testid={`session-card-${s.code}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B]">Kod</div>
                      <div className="font-display font-black text-3xl tracking-wider">{s.code}</div>
                    </div>
                    {statusBadge(s.status)}
                  </div>
                  <h4 className="font-display font-bold text-lg mt-2">{s.title}</h4>
                  {s.cross_scope_breakdown && s.cross_scope_breakdown.length > 0 && (
                    <div
                      className="brut bg-[#E0F2FE] text-[11px] tracking-wide font-bold px-2 py-1 rounded-md inline-flex items-center gap-1 mt-2"
                      title={s.cross_scope_breakdown
                        .map((b) => `${b.count} × ${b.label}`).join(" · ")}
                      data-testid={`session-crossscope-${s.code}`}
                    >
                      <span aria-hidden>🔀</span>
                      {s.cross_scope_breakdown.length === 1
                        ? `${s.cross_scope_breakdown[0].count} pyt. z: ${s.cross_scope_breakdown[0].label}`
                        : `${s.cross_scope_breakdown.reduce((a, b) => a + b.count, 0)} pyt. z ${s.cross_scope_breakdown.length} innych zakresów`}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 mt-3 text-sm text-[#334155]">
                    <span className="inline-flex items-center gap-1" data-testid={`session-time-${s.code}`}>
                      <Clock className="w-3 h-3" strokeWidth={2.5} />{s.time_limit_minutes} min
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <BookOpen className="w-3 h-3" strokeWidth={2.5} />{s.num_questions} pytań
                    </span>
                    <span
                      className="inline-flex items-center gap-1"
                      title={s.started_at ? "Rozpoczęto" : "Utworzono"}
                      data-testid={`session-started-${s.code}`}
                    >
                      <Calendar className="w-3 h-3" strokeWidth={2.5} />
                      {s.started_at
                        ? formatDateTime(s.started_at)
                        : <span className="text-[#64748B]">{formatDateTime(s.created_at)}</span>
                      }
                    </span>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Link to={`/teacher/session/${s.id}`} className="flex-1">
                      <Button className="w-full brut bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md" data-testid={`open-session-${s.code}`}>
                        Otwórz
                      </Button>
                    </Link>
                    <Button
                      onClick={() => {
                        if (!classes || classes.length === 0) {
                          // No classes at all — just duplicate keeping original assignment
                          runDuplicate(s);
                        } else {
                          setDuplicateSrc(s);
                        }
                      }}
                      disabled={duplicatingId === s.id}
                      variant="outline"
                      title="Duplikuj kartkówkę — wybierz klasę docelową"
                      className="brut bg-white border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#FEF08A] hover:text-[#0F172A] rounded-md"
                      data-testid={`duplicate-session-${s.code}`}
                    >
                      <Copy className={`w-4 h-4 ${duplicatingId === s.id ? "animate-pulse" : ""}`} strokeWidth={2.5} />
                    </Button>
                    <Button
                      onClick={() => setToDelete(s)}
                      variant="outline"
                      className="brut bg-white border-2 border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2] hover:text-[#EF4444] rounded-md"
                      data-testid={`delete-session-${s.code}`}
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                    </Button>
                  </div>
                </div>
              );

              return (
                <section
                  key={group.class_name}
                  data-testid={`group-${group.class_name}`}
                >
                  {/* Group header */}
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3 pb-2 border-b-2 border-dashed border-[#0F172A]">
                    <div className="flex items-center gap-2">
                      <span className={`brut w-9 h-9 flex items-center justify-center rounded-md ${
                        group.unassigned ? "bg-[#FEF08A]" : "bg-[#E0E7FF]"
                      }`}>
                        <GraduationCap className="w-4 h-4" strokeWidth={2.5} />
                      </span>
                      <h3 className="font-display font-black text-xl">{group.class_name}</h3>
                      <span className="text-xs text-[#64748B] font-bold">
                        ({group.items.length} {group.items.length === 1 ? "sesja" : "sesji"})
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {lobbyCount > 0 && (
                        <span className="brut bg-[#FEF08A] text-xs tracking-[0.15em] uppercase font-bold px-2 py-1 rounded-md">
                          Poczekalnia: {lobbyCount}
                        </span>
                      )}
                      {activeCount > 0 && (
                        <span className="brut bg-[#D1FAE5] text-xs tracking-[0.15em] uppercase font-bold px-2 py-1 rounded-md">
                          Aktywne: {activeCount}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Current school year sessions */}
                  {group.current.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {group.current.map(renderCard)}
                    </div>
                  )}

                  {/* If a class has no current-year sessions, hint the user */}
                  {group.current.length === 0 && group.pastYears.length > 0 && (
                    <div className="text-sm text-[#64748B] mb-3" data-testid={`no-current-${group.class_name}`}>
                      Brak kartkówek w bieżącym roku szkolnym.
                    </div>
                  )}

                  {/* Historic school years - collapsible, default closed */}
                  {group.pastYears.map((py) => {
                    const key = `${group.class_name}::${py.label}`;
                    const collapsed = isCollapsed(key);
                    return (
                      <div key={py.label} className="mt-4">
                        <button
                          type="button"
                          onClick={() => setCollapsed(key, !collapsed)}
                          className="brut brut-hover bg-[#F1F5F9] hover:bg-[#E2E8F0] w-full rounded-md p-3 flex items-center justify-between gap-3 text-left"
                          data-testid={`history-toggle-${group.class_name}-${py.label}`}
                          aria-expanded={!collapsed}
                        >
                          <span className="inline-flex items-center gap-2">
                            {collapsed
                              ? <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
                              : <ChevronDown className="w-4 h-4" strokeWidth={2.5} />}
                            <Archive className="w-4 h-4" strokeWidth={2.5} />
                            <span className="font-display font-bold">Rok szkolny {py.label}</span>
                          </span>
                          <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#64748B]">
                            {py.items.length} {py.items.length === 1 ? "sesja" : "sesji"}
                          </span>
                        </button>
                        {!collapsed && (
                          <div
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4"
                            data-testid={`history-grid-${group.class_name}-${py.label}`}
                          >
                            {py.items.map(renderCard)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </section>
              );
            })}
          </div>
        )}
      </main>

      <NewSessionDialog
        open={openCreate}
        onOpenChange={(o) => { setOpenCreate(o); if (!o) setDraftToOpen(null); }}
        classes={classes}
        initialDraft={draftToOpen}
        onCreated={(s) => { setOpenCreate(false); setDraftToOpen(null); nav(`/teacher/session/${s.id}`); }}
        onDraftSaved={() => { setDraftToOpen(null); load(); }}
      />

      <ClassesManager
        open={openClasses}
        onOpenChange={setOpenClasses}
        onChanged={() => { load(); loadOverview(scopeClass); }}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="brut bg-white rounded-md border-2 border-[#0F172A]" data-testid="delete-session-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black text-2xl">Usunąć sesję?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && (
                <>Sesja <b>{toDelete.code}</b> — {toDelete.title} — zostanie trwale usunięta wraz z wszystkimi wynikami uczniów. Tej akcji nie można cofnąć.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
              data-testid="delete-session-cancel-btn"
            >
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteSession}
              className="brut brut-hover bg-[#EF4444] hover:bg-[#EF4444] text-white font-bold rounded-md"
              data-testid="delete-session-confirm-btn"
            >
              Tak, usuń
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate → class picker */}
      {duplicateSrc && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
             onClick={() => setDuplicateSrc(null)}
             data-testid="duplicate-class-picker">
          <div className="brut border-4 border-[#0F172A] bg-white rounded-md max-w-md w-full p-6"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="font-display font-black text-xl">Duplikuj do klasy</h2>
                <p className="text-xs text-[#64748B] mt-1">
                  Kartkówka <b>{duplicateSrc.code}</b> — {duplicateSrc.title}
                </p>
              </div>
              <button onClick={() => setDuplicateSrc(null)}
                      className="brut brut-hover border-2 border-[#0F172A] rounded p-1 bg-white"
                      data-testid="duplicate-class-picker-close"
                      aria-label="Zamknij">
                <span aria-hidden>×</span>
              </button>
            </div>
            <p className="text-sm text-[#334155] mb-3">
              Nowa sesja dostanie własny kod. Wybierz klasę docelową:
            </p>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              <button
                onClick={() => runDuplicate(duplicateSrc)}
                disabled={duplicatingId === duplicateSrc.id}
                className="brut brut-hover w-full text-left bg-[#FEF08A] rounded-md px-3 py-2 font-bold text-sm flex items-center justify-between"
                data-testid="duplicate-class-same"
              >
                <span>
                  {duplicateSrc.class_id
                    ? <>↩ Ta sama klasa: <b>{duplicateSrc.class_name || "—"}</b></>
                    : <>↩ Bez przypisania (jak oryginał)</>}
                </span>
                <span className="text-[10px] uppercase tracking-wide">Domyślnie</span>
              </button>
              {duplicateSrc.class_id && (
                <button
                  onClick={() => runDuplicate(duplicateSrc, null)}
                  disabled={duplicatingId === duplicateSrc.id}
                  className="brut brut-hover w-full text-left bg-white rounded-md px-3 py-2 font-bold text-sm"
                  data-testid="duplicate-class-none"
                >
                  Bez przypisania
                </button>
              )}
              {classes
                .filter((c) => c.id !== duplicateSrc.class_id)
                .map((c) => (
                  <button
                    key={c.id}
                    onClick={() => runDuplicate(duplicateSrc, c.id)}
                    disabled={duplicatingId === duplicateSrc.id}
                    className="brut brut-hover w-full text-left bg-white rounded-md px-3 py-2 font-bold text-sm inline-flex items-center gap-2"
                    data-testid={`duplicate-class-${c.id}`}
                  >
                    <GraduationCap className="w-4 h-4" strokeWidth={2.5} />
                    {c.name}
                  </button>
                ))
              }
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!draftToDelete} onOpenChange={(o) => !o && setDraftToDelete(null)}>
        <AlertDialogContent className="brut bg-white rounded-md border-2 border-[#0F172A]" data-testid="delete-draft-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black text-2xl">Usunąć szkic?</AlertDialogTitle>
            <AlertDialogDescription>
              {draftToDelete && (
                <>Szkic „<b>{draftToDelete.name}</b>” — zostanie trwale usunięty. Tej akcji nie można cofnąć.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
              data-testid="delete-draft-cancel-btn"
            >
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteDraft}
              className="brut brut-hover bg-[#EF4444] hover:bg-[#EF4444] text-white font-bold rounded-md"
              data-testid="delete-draft-confirm-btn"
            >
              Tak, usuń
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ImpactStat({ label, value, hint, icon, color, badge, testId }) {
  return (
    <div
      className="brut border-2 border-[#0F172A] rounded-md p-3 flex flex-col justify-between"
      style={{ backgroundColor: color }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] tracking-wide uppercase font-black text-[#0F172A]">
          {icon}
          <span>{label}</span>
        </div>
        {badge && (
          <span className="brut bg-white text-[10px] font-black uppercase px-1.5 py-0.5 rounded">
            {badge}
          </span>
        )}
      </div>
      <div className="mt-2">
        <div className="font-display font-black text-3xl leading-none tabular-nums">
          {value}
        </div>
        <div className="text-[11px] text-[#334155] mt-1">{hint}</div>
      </div>
    </div>
  );
}

